/**
 * Utility functions for iframe height resizing and scroll handling (vanilla JS)
 */
export interface IframeResizeOptions {
  // Optional callback when height changes
  onHeightChange?: (height: number) => void;
  // Whether to enable wheel event handling for parent scrolling
  enableScrollEvents?: boolean;
  // Optional custom message processor for handling non-standard message formats
  customMessageProcessor?: (
    event: MessageEvent
  ) => { type: IframeMessageType; height?: number; deltaY?: number } | null;
}

// Add message type constants
export const IFRAME_MESSAGE_TYPES = {
  RESIZE: "resize",
  SCROLL: "scroll",
} as const;

export type IframeMessageType =
  (typeof IFRAME_MESSAGE_TYPES)[keyof typeof IFRAME_MESSAGE_TYPES];

/**
 * Checks if the event is an iframe message type
 */
export function isIframeMessage(event: MessageEvent): boolean {
  if (!event.data || typeof event.data !== "object") return false;

  return (
    event.data.type === IFRAME_MESSAGE_TYPES.RESIZE ||
    event.data.type === IFRAME_MESSAGE_TYPES.SCROLL
  );
}

/**
 * Creates a message handler for iframe height resizing and scroll events
 * @param iframe The iframe element to resize
 * @param options Configuration options
 * @returns A function to remove the event listener
 */
export function setupIframeHeightListener(
  iframe: HTMLIFrameElement,
  options: IframeResizeOptions = {}
): () => void {
  const {
    onHeightChange,
    enableScrollEvents = false,
    customMessageProcessor,
  } = options;

  const handleResizeMessage = (height: number | string) => {
    const numericHeight = Number(height);
    const iframeElement = iframe;

    // Update iframe height
    iframeElement.style.height = `${numericHeight}px`;
    iframeElement.style.minHeight = `${numericHeight}px`;

    if (onHeightChange) {
      onHeightChange(numericHeight);
    }
  };

  const handleScrollMessage = (deltaY: number) => {
    if (enableScrollEvents) {
      window.scrollBy({
        top: deltaY,
        behavior: "auto",
      });
    }
  };

  const processMessage = (
    messageType: IframeMessageType,
    data: { height?: number | string; deltaY?: number }
  ) => {
    switch (messageType) {
      case IFRAME_MESSAGE_TYPES.RESIZE:
        if (
          typeof data.height === "number" ||
          typeof data.height === "string"
        ) {
          handleResizeMessage(data.height);
        }
        break;

      case IFRAME_MESSAGE_TYPES.SCROLL:
        if (typeof data.deltaY === "number") {
          handleScrollMessage(data.deltaY);
        }
        break;

      default:
        // Ignore unrecognized message types
        break;
    }
  };

  const handleMessage = (event: MessageEvent) => {
    if (customMessageProcessor) {
      const processedMessage = customMessageProcessor(event);
      if (processedMessage) {
        processMessage(processedMessage.type, processedMessage);
        return;
      }
    }

    if (!event.data || typeof event.data !== "object") return;

    processMessage(event.data.type, event.data);
  };

  window.addEventListener("message", handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener("message", handleMessage);
  };
}

/**
 * Sends the height of an element to the parent window
 * @param element The element to measure
 * @param partSelectors Optional selectors for measuring by parts
 * @param heightBuffer Additional pixels to add to the height
 */
export function sendHeightToParent({
  element,
  partSelectors,
  heightBuffer = 0,
}: {
  element?: HTMLElement | null;
  partSelectors?: Record<string, string>;
  heightBuffer?: number;
} = {}): void {
  // If the current window is the parent, don't send the message
  if (window.parent === window) return;

  let height;

  // When partSelectors is provided, we measure individual sections separately and sum their heights.
  // This provides more accurate measurements than measuring the container alone, especially when:
  // 1. Some sections are scrollable (body) while others are fixed (header/footer)
  // 2. Content dynamically changes or loads asynchronously
  // 3. Complex layouts use fixed positioning or overflow properties
  if (partSelectors && Object.keys(partSelectors).length > 0) {
    let totalHeight = 0;
    const partHeights: Record<string, number> = {};

    Object.entries(partSelectors).forEach(([key, selector]) => {
      const el = document.querySelector(selector) as HTMLElement;
      if (el) {
        const partHeight = key.toLowerCase().includes("body")
          ? el.scrollHeight
          : el.getBoundingClientRect().height;

        totalHeight += partHeight;
        partHeights[key] = partHeight;
      }
    });

    if (totalHeight > 0) {
      height = totalHeight + heightBuffer;
    }
  }

  // If partSelectors is not provided, measure the provided element
  if (!height && element) {
    height = element.getBoundingClientRect().height + heightBuffer;
  } else if (!height) {
    // Fallback to document body
    height = document.body.scrollHeight;
  }

  if (height > 0) {
    window.parent.postMessage(
      {
        type: IFRAME_MESSAGE_TYPES.RESIZE,
        height,
      },
      "*"
    );
  }
}
